import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../components/table/table";
import BottomDrawer from "../../common/drawer/bottomDrawer";
import { useCommonFetch } from "../../../../hooks/useCommonFetch";
import { useEffect, useState } from "react";
import CommonForm from "../../../components/form/form";
import toast from "react-hot-toast";
import {
  createSalaryIncrement,
  deleteSalaryIncrement,
  getSalaryIncrementList,
  updateSalaryIncrement,
} from "../../../../services/workspace/salaryIncrement.service";
import { getUserList } from "../../../../services/workspace/user.service";
import { convertToLabelValue } from "../../../../utils/common";
import { createActionButtons, commonConditions } from "../../../../utils/actionButtonHelpers";
import { HiCurrencyRupee, HiTrendingUp, HiTrendingDown } from "react-icons/hi";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { fetchStaffList } from "../../../../store/slices/user.slice";

/**
 * ⚠️ IMPORTANT: Salary Increment & Salary Structure Integration
 * 
 * This system manages annual salary increments while keeping position/designation unchanged.
 * 
 * CRITICAL: Backend must sync increment with salary structure assignment:
 * - When increment is created, backend should also create/update salary structure assignment
 * - New CTC should be reflected in the structure assignment
 * - Effective date should match increment effective date
 * 
 * This ensures:
 * ✅ Salary Structure system shows updated CTC
 * ✅ Increment history is maintained separately
 * ✅ Both systems work together without conflicts
 * 
 * See: SALARY_INCREMENT_GUIDE.md for complete integration details
 */

const actions_type = {
  DELETE: "DELETE",
  ADD_EDIT: "ADD_EDIT",
};

const items = [{ label: "Salary Increment Management", path: "" }];

const TableHeaderData = [
  { label: "Staff Name", key: "staff_name" },
  { label: "Employee ID", key: "employee_id" },
  { label: "Department", key: "department" },
  { label: "Designation", key: "designation" },
  { label: "Previous CTC", key: "previous_ctc" },
  { label: "New CTC", key: "new_ctc" },
  { label: "Increment Amount", key: "increment_amount" },
  { label: "Increment %", key: "increment_percentage" },
  { label: "Effective Date", key: "effective_date" },
  { label: "Increment Year", key: "increment_year" },
  { label: "Created At", key: "created_at" },
];

const ManageSalaryIncrement = () => {
  const [isEditValue, setIsEditValue] = useState(null);
  const dispatch = useDispatch();
  const { staffList, staffLoading } = useSelector((state) => state.user);

  // Fetch staff list on mount
  useEffect(() => {
    dispatch(fetchStaffList({ type: 2, additional_info: true }));
  }, [dispatch]);

  const staffOptions =
    staffList?.user?.data?.map((user) => ({
      label: `${user?.name} (${user?.code || "N/A"})`,
      value: user?.id,
    })) || [];

  const {
    loading,
    data: tableData,
    total: totalData,
    currentPage,
    search,
    selectedId,
    action,
    handleSearchChange,
    handlePageChange,
    openModal,
    closeModal,
    handleCreate,
    handleUpdate,
    handleDelete,
    sortingUIProps,
  } = useCommonFetch(getSalaryIncrementList, {
    dataPath: "increment.data",
    totalPath: "increment.meta.total",
    errorMessage: "Failed to fetch salary increment data!",
    debounceDelay: 500,
    sortingOptions: [
      { label: "Date", field: "created_at" },
      { label: "Effective Date", field: "effective_date" },
    ],
    sortingModule: "salaryIncrement",
    sortingDisableText: "Disabled",
    sortingShowDisableToggle: false,
    dataTransformer: (data) => {
      if (Array.isArray(data)) {
        const transformedData = data.map((item) => {
          const incrementAmount = item.new_ctc - item.previous_ctc;
          const incrementPercentage = item.previous_ctc > 0
            ? ((incrementAmount / item.previous_ctc) * 100).toFixed(2)
            : 0;

          return {
            ...item,
            id: {
              data: item.uuid,
              className: "",
              sticky: false,
              variant: "text",
              customCases: null,
            },
            staff_name: {
              data: item.staff?.name || item.staff_name || "-",
              className: "",
              sticky: false,
              variant: "name",
              customCases: null,
            },
            employee_id: {
              data: item.staff?.code || item.employee_id || "-",
              className: "",
              sticky: false,
              variant: "text",
              customCases: null,
            },
            department: {
              data: item.staff?.department?.name || item.department || "-",
              className: "",
              sticky: false,
              variant: "text",
              customCases: null,
            },
            designation: {
              data: item.staff?.designation?.name || item.designation || "-",
              className: "",
              sticky: false,
              variant: "text",
              customCases: null,
            },
            previous_ctc: {
              data: item.previous_ctc,
              className: "",
              sticky: false,
              variant: "currency",
              customCases: null,
            },
            new_ctc: {
              data: item.new_ctc,
              className: "",
              sticky: false,
              variant: "currency",
              customCases: null,
            },
            increment_amount: {
              data: incrementAmount,
              className: "text-green-600 font-semibold",
              sticky: false,
              variant: "currency",
              customCases: null,
            },
            increment_percentage: {
              data: incrementPercentage,
              className: "text-green-600 font-semibold",
              sticky: false,
              variant: "text",
              customCases: (percentage) => {
                return `${percentage}%`;
              },
            },
            effective_date: {
              data: item.effective_date,
              className: "",
              sticky: false,
              variant: "date",
              customCases: null,
            },
            increment_year: {
              data: item.increment_year || (item.effective_date ? moment(item.effective_date).format("YYYY") : "-"),
              className: "",
              sticky: false,
              variant: "text",
              customCases: null,
            },
            created_at: {
              data: item.created_at,
              className: "",
              sticky: false,
              variant: "date-time",
              customCases: null,
            },
          };
        });

        const actionButton = createActionButtons({
          edit: {
            handler: (item) => {
              openModal(item, actions_type.ADD_EDIT);
              setIsEditValue({
                id: item.id.data,
                staff_id: item.staff?.id || item.staff_id,
                previous_ctc: item.previous_ctc,
                new_ctc: item.new_ctc,
                effective_date: item.effective_date,
                increment_year: item.increment_year,
                remarks: item.remarks || "",
              });
            },
            show: commonConditions.always,
          },
          delete: {
            handler: (item) => {
              handleDelete(item.id.data, deleteSalaryIncrement, {
                successMessage: "Salary increment deleted successfully!",
                errorMessage: "Failed to delete salary increment!",
              });
            },
            show: commonConditions.always,
          },
        });

        return {
          data: transformedData,
          action: actionButton,
        };
      }
      return { data: [], action: {} };
    },
  });

  // Form fields
  const comprehensiveFields = [
    {
      name: "staff_id",
      label: "Staff Member",
      type: "select",
      options: staffOptions,
      required: true,
      disabled: staffLoading || !!isEditValue, // Disable in edit mode
      placeholder: staffLoading ? "Loading staff..." : "Select staff member",
      colSpan: 6,
    },
    {
      name: "previous_ctc",
      label: "Previous CTC (Annual)",
      type: "number",
      required: true,
      placeholder: "Enter previous annual CTC",
      min: 0,
      step: 1000,
      colSpan: 6,
    },
    {
      name: "new_ctc",
      label: "New CTC (Annual)",
      type: "number",
      required: true,
      placeholder: "Enter new annual CTC",
      min: 0,
      step: 1000,
      colSpan: 6,
    },
    {
      name: "effective_date",
      label: "Effective Date",
      type: "date",
      required: true,
      placeholder: "Select effective date",
      colSpan: 6,
    },
    {
      name: "increment_year",
      label: "Increment Year",
      type: "number",
      required: true,
      placeholder: "e.g., 2024",
      min: 2000,
      max: 2100,
    },
    {
      name: "remarks",
      label: "Remarks",
      type: "textarea",
      required: false,
      placeholder: "Add any remarks or notes",
      rows: 3,
    },
  ];

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const submitData = {
      staff_id: values.staff_id?.value || values.staff_id,
      previous_ctc: parseFloat(values.previous_ctc),
      new_ctc: parseFloat(values.new_ctc),
      effective_date: values.effective_date,
      increment_year: parseInt(values.increment_year),
      remarks: values.remarks || "",
    };

    // Validate increment
    if (submitData.new_ctc <= submitData.previous_ctc) {
      toast.error("New CTC must be greater than previous CTC!");
      setSubmitting(false);
      return;
    }

    // ⚠️ IMPORTANT NOTE FOR BACKEND:
    // When creating/updating increment, backend should also:
    // 1. Get staff's current salary structure assignment
    // 2. Create/update salary structure assignment with new CTC
    // 3. Set effective_date to match increment effective_date
    // 4. This ensures salary structure system stays in sync with increments
    // See: SALARY_INCREMENT_GUIDE.md for detailed implementation notes

    if (isEditValue) {
      await handleUpdate(submitData, isEditValue.id, updateSalaryIncrement, {
        successMessage: "Salary increment updated successfully!",
        errorMessage: "Failed to update salary increment!",
      });
    } else {
      await handleCreate(submitData, createSalaryIncrement, {
        successMessage: "Salary increment created successfully!",
        errorMessage: "Failed to create salary increment!",
      });
    }

    setSubmitting(false);
    setTimeout(() => {
      resetForm();
      setIsEditValue(null);
    }, 1000);
  };

  // Handle form cancellation
  const handleCancel = () => {
    setIsEditValue(null);
    closeModal();
  };

  return (
    <div className="flex flex-col gap-2">
      <PageTableHeader
        isSearch
        onSearchChange={handleSearchChange}
        add
        onClickAdd={() => openModal("", actions_type.ADD_EDIT)}
        shorting
        breadcrumbs={items}
        sortingModule="salaryIncrement"
        onSortingChange={sortingUIProps?.onSortingChange}
        sortingProps={sortingUIProps}
      />

      <div className="bg-white">
        <CommonTable
          search={search}
          TableHeaderData={TableHeaderData}
          tabledata={tableData.data}
          currentPage={currentPage}
          totalData={totalData}
          setCurrentPage={handlePageChange}
          loading={loading}
          action={tableData.action}
        />

        {action === actions_type.ADD_EDIT && (
          <BottomDrawer
            isOpen={action === actions_type.ADD_EDIT}
            setIsOpen={handleCancel}
            title={selectedId ? "Edit Salary Increment" : "Add Salary Increment"}
            classNames="!w-[600px]"
            position="right"
          >
            <div className="flex flex-col gap-4 overflow-y-auto px-2 py-4">
              <CommonForm
                fields={comprehensiveFields}
                initialData={isEditValue ? isEditValue : {}}
                onSubmit={handleSubmit}
                submitButtonText={selectedId ? "Update Increment" : "Create Increment"}
                onCancel={handleCancel}
                cancelButtonText="Cancel"
                columnsPerRow={1}
                gap="gap-4"
                rowClassName=""
                fieldClassName="bg-white p-3 rounded shadow-sm"
              />
            </div>
          </BottomDrawer>
        )}
      </div>
    </div>
  );
};

export default ManageSalaryIncrement;

